import { Navigate, Route, Routes } from 'react-router-dom';
import { ClinicianLogin } from './pages/ClinicianLogin.js';
import { ClinicianDashboard } from './pages/ClinicianDashboard.js';
import { ExercisePreview } from './pages/ExercisePreview.js';
import { PatientList } from './pages/clinician/PatientList.js';
import { PrescriptionBuilder } from './pages/clinician/PrescriptionBuilder.js';
import { ClinicianLayout } from './components/ClinicianLayout.js';
import { TherapyLogin } from './pages/therapy/TherapyLogin.js';
import { TherapyHome } from './pages/therapy/TherapyHome.js';
import { SessionRunner } from './pages/therapy/SessionRunner.js';

/**
 * Route map. Phase 1 covers the clinician login and a placeholder dashboard —
 * enough to prove auth and deployment end to end.
 *
 * /clinician/preview is the exercise library. It began as Phase 2 scaffolding
 * and was kept deliberately (DECISIONS.md 2026-08-21): it answers what an
 * exercise looks like and what it measures, without opening a patient's chart.
 * It saves nothing and prescribes nothing.
 *
 * /therapy is the patient side (Phase 4) and shares nothing with the clinician
 * routes above but the desktop gate and the theme. Separate cookie, separate
 * guard: a clinician login grants nothing there, and a patient login grants
 * nothing here.
 *
 * THE ROOT BELONGS TO THE PATIENT. Typing the bare domain lands on /therapy;
 * the clinician adds /clinician. Decided 2026-08-22 — the address is long
 * enough already without a path on the end, and the people typing it are the
 * ones least able to absorb the friction.
 *
 * Still to come:
 *   Phase 8  /clinician/runner  (in-office runner)
 */
export function App() {
  return (
    <Routes>
      {/* The bare domain is the PATIENT's door. therapy.pathwaysbsc.com is
          already a long thing to type, and asking a patient to add /therapy on
          the end of it is friction on the one population least able to absorb
          it. The doctor types /clinician; there is one of him and many of
          them. */}
      <Route path="/" element={<Navigate to="/therapy" replace />} />
      {/* Login sits OUTSIDE the shell: it must not show navigation to places
          you cannot yet go. */}
      <Route path="/clinician/login" element={<ClinicianLogin />} />

      {/* Everything else shares one header, one navigation, one sign-out. */}
      <Route element={<ClinicianLayout />}>
        <Route path="/clinician" element={<ClinicianDashboard />} />
        <Route path="/clinician/patients" element={<PatientList />} />
        <Route path="/clinician/patients/:patientId" element={<PrescriptionBuilder />} />
        <Route path="/clinician/preview" element={<ExercisePreview />} />
      </Route>
      {/* The patient side. No shared shell: there is no navigation to offer a
          patient, and every screen they see is one step of one session. */}
      <Route path="/therapy" element={<TherapyLogin />} />
      <Route path="/therapy/home" element={<TherapyHome />} />
      <Route path="/therapy/session" element={<SessionRunner />} />

      {/* Anything unrecognised goes the same way, for the same reason. */}
      <Route path="*" element={<Navigate to="/therapy" replace />} />
    </Routes>
  );
}
